import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import JsBarcode from 'jsbarcode';

import { TipoPessoaEnum } from '@shared/enums/pessoa/tipo-pessoa.enum';
import { LayoutImpressaoVendaEnum } from '@shared/enums/vendas/layout-impressao-venda.enum';
import { TipoImpostoImpressaoEnum } from '@shared/enums/vendas/tipo-imposto-impressao.enum';
import { TipoImpressaoImagemEnum } from '@shared/enums/vendas/tipo-impressao-imagem.enum';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { CpfCnpjPipe } from '@shared/pipes/cpf-cnpj.pipe';
import { FoneMaskPipe } from '@shared/pipes/fone-mask.pipe';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { VendaFolhaClienteStruct } from './structs/venda-folha-cliente.struct';
import { VendaFolhaDadosStruct } from './structs/venda-folha-dados.struct';
import { VendaFolhaDocumentoStruct } from './structs/venda-folha-documento.struct';
import { VendaFolhaItemStruct } from './structs/venda-folha-item.struct';
import { VendaFolhaPagamentoStruct } from './structs/venda-folha-pagamento.struct';

/** Linha da grade de itens: item avulso, ou o kit como agrupador com os componentes abaixo. */
interface VflBlocoItens {
    item: VendaFolhaItemStruct | null;
    componentes: VendaFolhaItemStruct[];
    totalKit: number;
}

/**
 * Folha do documento comercial (§11), a mesma na prévia de impressão do sistema e na página pública do
 * link. O Orçamento tem modelo único; o Pedido de Venda escolhe entre padrão, detalhado e recibo. Cada
 * configuração de impressão liga ou desliga só o elemento que ela descreve (CA 20).
 *
 * A folha é uma tabela externa: o `thead` (emitente, título e código de barras) repete em toda página e o
 * `tbody` leva o conteúdo. A numeração "Página n de m" sai das margens do `@page` da janela de impressão.
 * Casas decimais vêm de `dados` (a página pública não tem usuário logado), por isso todo `abnt` recebe as casas.
 */
@Component({
    selector: 'app-venda-folha',
    standalone: true,
    imports: [CommonModule, AbntPipe, CpfCnpjPipe, FoneMaskPipe],
    templateUrl: './venda-folha.component.html',
    styleUrls: ['./venda-folha.component.css'],
})
export class VendaFolhaComponent implements OnChanges, OnDestroy {
    @Input({ required: true }) public dados!: VendaFolhaDadosStruct;
    @Input() public layout: number = LayoutImpressaoVendaEnum.Padrao;

    public readonly layoutEnum = LayoutImpressaoVendaEnum;

    public venda: VendaFolhaDocumentoStruct | null = null;
    public cliente: VendaFolhaClienteStruct | null = null;
    public nomeVendedor: string | null = null;
    public blocos: VflBlocoItens[] = [];
    public pagamentos: VendaFolhaPagamentoStruct[] = [];
    public itensComImagem: VendaFolhaItemStruct[] = [];
    public somaQuantidades = 0;
    public pesoBruto = 0;
    public pesoLiquido = 0;

    public logoUrl = '';
    public nomeEmpresa = '';
    public nomeFantasiaEmpresa = '';
    public cnpjEmpresa = '';
    public inscricaoEstadualEmpresa = '';
    public linhasEnderecoEmpresa: string[] = [];
    public enderecoEmpresaLinhaUnica = '';
    /** Preço unitário com as casas de preço da empresa; a folha não leva o símbolo da moeda (protótipos). */
    public casasPreco = 2;
    public casasQuantidade = 2;

    /** Rótulos abreviados da linha de tributos do item, na grafia do protótipo. */
    private readonly rotulosImposto: Record<number, string> = {
        [TipoImpostoImpressaoEnum.AliquotaIcms]: 'Alíq. ICMS',
        [TipoImpostoImpressaoEnum.ValorIcms]: 'Valor ICMS',
        [TipoImpostoImpressaoEnum.AliquotaIcmsSt]: 'Alíq. ICMS ST',
        [TipoImpostoImpressaoEnum.ValorIcmsSt]: 'Valor ICMS ST',
        [TipoImpostoImpressaoEnum.Mva]: 'MVA',
        [TipoImpostoImpressaoEnum.AliquotaFcp]: 'Alíq. FCP',
        [TipoImpostoImpressaoEnum.ValorFcp]: 'Valor FCP',
        [TipoImpostoImpressaoEnum.AliquotaFcpSt]: 'Alíq. FCP ST',
        [TipoImpostoImpressaoEnum.ValorFcpSt]: 'Valor FCP ST',
        [TipoImpostoImpressaoEnum.AliquotaIpi]: 'Alíq. IPI',
        [TipoImpostoImpressaoEnum.ValorIpi]: 'Valor IPI',
        [TipoImpostoImpressaoEnum.AliquotaPis]: 'Alíq. PIS',
        [TipoImpostoImpressaoEnum.ValorPis]: 'Valor PIS',
        [TipoImpostoImpressaoEnum.AliquotaCofins]: 'Alíq. COFINS',
        [TipoImpostoImpressaoEnum.ValorCofins]: 'Valor COFINS',
        [TipoImpostoImpressaoEnum.CstIbsCbs]: 'CST IBS/CBS',
        [TipoImpostoImpressaoEnum.ClassificacaoTributaria]: 'cClassTrib',
        [TipoImpostoImpressaoEnum.BaseIbsCbs]: 'Base IBS/CBS',
        [TipoImpostoImpressaoEnum.IbsUf]: 'IBS UF',
        [TipoImpostoImpressaoEnum.IbsMunicipio]: 'IBS Mun.',
        [TipoImpostoImpressaoEnum.IbsTotal]: 'IBS',
        [TipoImpostoImpressaoEnum.Cbs]: 'CBS',
        [TipoImpostoImpressaoEnum.Cfop]: 'CFOP',
    };

    private readonly abnt: AbntPipe;
    private observadorPrevia?: ResizeObserver;

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        authService: AuthService,
    ) {
        // Só formatação: as casas decimais sempre vão por parâmetro.
        this.abnt = new AbntPipe(authService);
    }

    /**
     * O código de barras é desenhado direto no `<svg>` (o SVG sobrevive à cópia do HTML para a janela de
     * impressão). O `<svg>` nasce e morre com o layout escolhido, por isso o setter.
     */
    @ViewChild('vflCodigoBarras')
    public set codigoBarras(ref: ElementRef<SVGSVGElement> | undefined) {
        if (!ref || !this.venda) return;

        JsBarcode(ref.nativeElement, String(this.venda.numero), {
            format: 'CODE128',
            displayValue: false,
            height: 40,
            width: 1.6,
            margin: 0,
        });
    }

    /**
     * Encaixa a folha na largura da prévia (A4 inteiro no celular, como um visualizador de PDF; nunca maior que o
     * tamanho real). Só a tela muda: o zoom fica na `.vfl-papel` e a janela de impressão leva o conteúdo de dentro
     * dela. Observa também a folha, porque o recibo é mais estreito; a largura natural é a medida atual dividida
     * pelo zoom aplicado. O estilo vai direto no elemento: o callback do ResizeObserver roda fora da detecção de
     * mudanças.
     */
    @ViewChild('vflPrevia')
    public set previa(ref: ElementRef<HTMLElement> | undefined) {
        this.observadorPrevia?.disconnect();
        if (!ref) return;

        const previa = ref.nativeElement;
        const papel = previa.querySelector<HTMLElement>('.vfl-papel');
        if (!papel) return;

        this.observadorPrevia = new ResizeObserver(() => {
            const zoomAtual = parseFloat(papel.style.zoom) || 1;
            const estilo = getComputedStyle(previa);
            const disponivel = previa.clientWidth - parseFloat(estilo.paddingLeft) - parseFloat(estilo.paddingRight);
            const natural = papel.getBoundingClientRect().width / zoomAtual;
            const zoom = Math.min(1, disponivel / natural);

            if (Math.abs(zoom - zoomAtual) > 0.01) papel.style.zoom = String(zoom);
        });
        this.observadorPrevia.observe(previa);
        this.observadorPrevia.observe(papel);
    }

    public ngOnChanges(): void {
        if (!this.dados) return;

        this.casasPreco = this.dados.casasDecimaisPreco || 2;
        this.casasQuantidade = this.dados.casasDecimaisQuantidade || 2;
        this.cliente = this.dados.cliente;
        this.nomeVendedor = this.dados.nomeVendedor;
        this.montarEmpresa();
        this.montarDocumento(this.dados.documento);
    }

    public ngOnDestroy(): void {
        this.observadorPrevia?.disconnect();
    }

    private montarEmpresa(): void {
        const empresa = this.dados.empresa;

        this.logoUrl = empresa.urlLogo || '';
        this.nomeEmpresa = empresa.nomeEmpresa || '';
        // CNPJ e CEP chegam formatados da sessão (impressão do sistema) e crus da página pública: formata aqui.
        this.cnpjEmpresa = new CpfCnpjPipe().transform(empresa.cnpj || '') || '';
        this.nomeFantasiaEmpresa = empresa.nomeFantasia || '';
        this.inscricaoEstadualEmpresa = empresa.inscricaoEstadual || '';

        const rua = [empresa.logradouro, empresa.numero, empresa.complemento].filter((v) => !!v).join(', ');
        const municipioUf = [empresa.municipio, empresa.uf].filter((v) => !!v).join(' - ');
        const cep = this.formatarCep(empresa.cep);

        this.linhasEnderecoEmpresa = [rua, [empresa.bairro, municipioUf].filter((v) => !!v).join(', '), cep]
            .filter((v) => !!v);
        this.enderecoEmpresaLinhaUnica = [rua, [empresa.bairro, empresa.municipio, empresa.uf].filter((v) => !!v).join(', '), cep]
            .filter((v) => !!v)
            .join(' - ');
    }

    /** Itens na sequência gravada (§6.2.4); o kit entra na posição do primeiro componente. */
    private montarDocumento(venda: VendaFolhaDocumentoStruct): void {
        const itens = [...(venda.itens ?? [])].sort((a, b) => a.sequencia - b.sequencia);
        const blocos: VflBlocoItens[] = [];
        const kits = new Map<number, VflBlocoItens>();

        for (const item of itens) {
            if (item.numeroKit == null) {
                blocos.push({ item, componentes: [], totalKit: 0 });
                continue;
            }

            let kit = kits.get(item.numeroKit);
            if (!kit) {
                kit = { item: null, componentes: [], totalKit: 0 };
                kits.set(item.numeroKit, kit);
                blocos.push(kit);
            }
            kit.componentes.push(item);
            kit.totalKit += item.valorTotal || 0;
        }

        this.venda = venda;
        this.blocos = blocos;
        this.itensComImagem = itens.filter((item) => !!item.urlImagemCapa);
        this.somaQuantidades = itens.reduce((soma, item) => soma + (item.quantidade || 0), 0);

        // Peso é por unidade do produto: acompanha a quantidade (regra de venda-totais.calculo.ts).
        this.pesoBruto = itens.reduce((soma, item) => soma + (item.pesoBruto || 0) * (item.quantidade || 0), 0);
        this.pesoLiquido = itens.reduce((soma, item) => soma + (item.pesoLiquido || 0) * (item.quantidade || 0), 0);

        this.pagamentos = [...(venda.pagamentos ?? [])]
            .sort((a, b) => a.sequencia - b.sequencia)
            .map((pagamento) => ({
                ...pagamento,
                parcelas: [...(pagamento.parcelas ?? [])].sort((a, b) => a.numeroParcela - b.numeroParcela),
            }));
    }

    // ---- Documento ----

    public get isPedido(): boolean {
        return this.dados.documento.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda;
    }

    public get tituloDocumento(): string {
        return this.isPedido ? 'Pedido de Venda' : 'Orçamento';
    }

    /** Modelo nunca chega à folha (o servidor recusa imprimir e compartilhar modelo), então o número sai puro. */
    public get numeroDocumento(): string {
        return this.venda ? `${this.venda.numero}` : '';
    }

    public get totalItens(): number {
        return this.venda?.valorTotalItens ?? this.blocos.reduce((soma, bloco) =>
            soma + (bloco.item ? bloco.item.valorTotal || 0 : bloco.totalKit), 0);
    }

    public get acrescimos(): number {
        return (this.venda?.valorFrete || 0) + (this.venda?.valorDespesas || 0);
    }

    public get temParcelas(): boolean {
        return this.pagamentos.some((pagamento) => pagamento.parcelas.length > 0);
    }

    public get temIntermediador(): boolean {
        return this.pagamentos.some((pagamento) => pagamento.idIntermediadorPagamento != null);
    }

    public get temBandeira(): boolean {
        return this.pagamentos.some((pagamento) => pagamento.idIntermediadorBandeira != null);
    }

    // ---- Configurações de impressão (§11.2 e §11.3): cada uma liga só o seu elemento ----

    /** [PED] Nome fantasia no lugar da razão social, com a razão como reserva. */
    public get nomeEmitente(): string {
        if (this.isPedido && this.dados.configPedidoVenda?.isImprimirNomeFantasia) {
            return this.nomeFantasiaEmpresa || this.nomeEmpresa;
        }
        return this.nomeEmpresa;
    }

    /** [PED] Endereço da empresa nos três layouts; o Orçamento sempre imprime. */
    public get isImprimirEnderecoEmpresa(): boolean {
        return this.isPedido ? !!this.dados.configPedidoVenda?.isImprimirEnderecoEmpresa : true;
    }

    /** [PED] Code 128 do número sob o título, no padrão e no detalhado. */
    public get isImprimirCodigoBarras(): boolean {
        return this.isPedido
            && this.layout !== LayoutImpressaoVendaEnum.Recibo
            && !!this.dados.configPedidoVenda?.isImprimirCodigoBarras;
    }

    public get isImprimirDescComplementar(): boolean {
        if (!this.isPedido) return !!this.dados.configOrcamento?.imprimirDescComplementar;

        return this.layout !== LayoutImpressaoVendaEnum.Recibo && !!this.dados.configPedidoVenda?.isImprimirDescComplementar;
    }

    /** [ORC] NCM sob o SKU. */
    public get isImprimirNcm(): boolean {
        return !this.isPedido && !!this.dados.configOrcamento?.imprimirNcm;
    }

    /** [ORC] "Enviado por" com o e-mail de quem envia (quem imprime no sistema; no link, o vendedor). */
    public get isImprimirEmailUsuario(): boolean {
        return !this.isPedido && !!this.dados.configOrcamento?.imprimirEmailUsuario && !!this.dados.emailEnviadoPor;
    }

    /** Imagem de capa antes do nome: [ORC] "ao lado da descrição"; [PED] só no detalhado. */
    public get isImprimirImagemNoItem(): boolean {
        if (!this.isPedido) {
            return this.dados.configOrcamento?.idTipoImpressaoImagem === TipoImpressaoImagemEnum.AoLadoDaDescricao;
        }
        return this.layout === LayoutImpressaoVendaEnum.Detalhado && !!this.dados.configPedidoVenda?.isImprimirImagemProdutos;
    }

    /** [ORC] Seção "Imagens" no fim, só com os itens que têm capa. */
    public get isImprimirSecaoImagens(): boolean {
        return !this.isPedido
            && this.dados.configOrcamento?.idTipoImpressaoImagem === TipoImpressaoImagemEnum.EmSecaoSeparada
            && this.itensComImagem.length > 0;
    }

    /** Tributos por item: exigem calcular impostos e imprimir impostos; [PED] só no detalhado. */
    public get isImprimirImpostos(): boolean {
        if (this.listaImpostos.length === 0) return false;

        if (!this.isPedido) {
            return !!this.dados.configOrcamento?.calcularImpostos && !!this.dados.configOrcamento?.imprimirImpostosItens;
        }
        return this.layout === LayoutImpressaoVendaEnum.Detalhado
            && !!this.dados.configPedidoVenda?.isCalcularImpostos
            && !!this.dados.configPedidoVenda?.isImprimirImpostosItens;
    }

    /** [PED] Linhas de data e assinatura do recebedor, no padrão e no detalhado. */
    public get isImprimirCamposAssinatura(): boolean {
        return this.isPedido
            && this.layout !== LayoutImpressaoVendaEnum.Recibo
            && !!this.dados.configPedidoVenda?.isImprimirCamposAssinatura;
    }

    /** Ids escolhidos nas configurações, na ordem do catálogo. */
    private get listaImpostos(): number[] {
        return [...(this.dados.listImpostoImpressao ?? [])].sort((a, b) => a - b);
    }

    // ---- Item ----

    public nomeDoItem(item: VendaFolhaItemStruct): string {
        return item.descricao || item.descricaoProduto || '';
    }

    public nomeDoKit(bloco: VflBlocoItens): string {
        return bloco.componentes[0]?.descricaoProdutoKit || 'Kit';
    }

    public skuDoKit(bloco: VflBlocoItens): string {
        return bloco.componentes[0]?.codigoSkuProdutoKit || '';
    }

    public quantidadeDoKit(bloco: VflBlocoItens): number {
        return bloco.componentes[0]?.quantidadeKit || 1;
    }

    /** Preço fixo usa o preço do kit; o kit por soma é o total dividido pela quantidade de kits. */
    public precoDoKit(bloco: VflBlocoItens): number {
        return bloco.componentes[0]?.precoKit ?? bloco.totalKit / this.quantidadeDoKit(bloco);
    }

    /**
     * A linha de tributos do item com os ids escolhidos, lidos de `item.imposto`; item sem imposto gravado
     * imprime zeros (e "–" nos códigos).
     */
    public impostosDoItem(item: VendaFolhaItemStruct): { rotulo: string; valor: string }[] {
        return this.listaImpostos.map((id) => ({ rotulo: this.rotulosImposto[id] ?? '', valor: this.valorDoImposto(item, id) }));
    }

    private valorDoImposto(item: VendaFolhaItemStruct, id: number): string {
        const imposto = item.imposto;
        switch (id) {
            case TipoImpostoImpressaoEnum.AliquotaIcms: return this.percentual(imposto?.percentualAliquotaIcms);
            case TipoImpostoImpressaoEnum.ValorIcms: return this.numero(imposto?.valorIcms);
            case TipoImpostoImpressaoEnum.AliquotaIcmsSt: return this.percentual(imposto?.percentualAliquotaIcmsSt);
            case TipoImpostoImpressaoEnum.ValorIcmsSt: return this.numero(imposto?.valorIcmsSt);
            case TipoImpostoImpressaoEnum.Mva: return this.percentual(imposto?.percentualMvaIcmsSt);
            case TipoImpostoImpressaoEnum.AliquotaFcp: return this.percentual(imposto?.percentualFcp);
            case TipoImpostoImpressaoEnum.ValorFcp: return this.numero(imposto?.valorFcp);
            case TipoImpostoImpressaoEnum.AliquotaFcpSt: return this.percentual(imposto?.percentualFcpSt);
            case TipoImpostoImpressaoEnum.ValorFcpSt: return this.numero(imposto?.valorFcpSt);
            case TipoImpostoImpressaoEnum.AliquotaIpi: return this.percentual(imposto?.percentualAliquotaIpi);
            case TipoImpostoImpressaoEnum.ValorIpi: return this.numero(imposto?.valorIpi);
            case TipoImpostoImpressaoEnum.AliquotaPis: return this.percentual(imposto?.percentualAliquotaPis);
            case TipoImpostoImpressaoEnum.ValorPis: return this.numero(imposto?.valorPis);
            case TipoImpostoImpressaoEnum.AliquotaCofins: return this.percentual(imposto?.percentualAliquotaCofins);
            case TipoImpostoImpressaoEnum.ValorCofins: return this.numero(imposto?.valorCofins);
            case TipoImpostoImpressaoEnum.CstIbsCbs: return imposto?.codigoCstIbsCbs || '–';
            case TipoImpostoImpressaoEnum.ClassificacaoTributaria: return imposto?.codigoClassificacaoTributariaIbsCbs || '–';
            case TipoImpostoImpressaoEnum.BaseIbsCbs: return this.numero(imposto?.valorBaseCalculoIbsCbs);
            case TipoImpostoImpressaoEnum.IbsUf: return this.numero(imposto?.valorIbsUf);
            case TipoImpostoImpressaoEnum.IbsMunicipio: return this.numero(imposto?.valorIbsMunicipal);
            case TipoImpostoImpressaoEnum.IbsTotal: return this.numero(imposto?.valorIbs);
            case TipoImpostoImpressaoEnum.Cbs: return this.numero(imposto?.valorCbs);
            case TipoImpostoImpressaoEnum.Cfop: return item.codigoCfop || '–';
            default: return '–';
        }
    }

    private percentual(valor: number | null | undefined): string {
        return `${this.abnt.transform(valor ?? 0, 'quantidade', 2)}%`;
    }

    private numero(valor: number | null | undefined): string {
        return this.abnt.transform(valor ?? 0, 'quantidade', 2);
    }

    // ---- Lookups do pagamento ----

    public nomeFormaRecebimento(idFormaRecebimento: number | null): string {
        return this.dados.financas?.listaFormaRecebimento.find((forma) => forma.id === idFormaRecebimento)?.descricao || '–';
    }

    public nomeCondicao(pagamento: VendaFolhaPagamentoStruct): string {
        if (pagamento.condicaoPagamentoLivre) return pagamento.condicaoPagamentoLivre;

        return this.dados.financas?.listaCondicaoPagamento
            .find((condicao) => condicao.idCondicaoPagamento === pagamento.idCondicaoPagamento)?.nome || '–';
    }

    public nomeIntermediador(pagamento: VendaFolhaPagamentoStruct): string {
        return this.dados.financas?.listaIntermediadorPagamento
            .find((intermediador) => intermediador.idIntermediadorPagamento === pagamento.idIntermediadorPagamento)?.descricao || '–';
    }

    /** A bandeira vem das aceitas pelo intermediador ou, na falta, das condições comerciais dele. */
    public nomeBandeira(pagamento: VendaFolhaPagamentoStruct): string {
        if (pagamento.idIntermediadorBandeira == null) return '–';

        const intermediador = this.dados.financas?.listaIntermediadorPagamento
            .find((i) => i.idIntermediadorPagamento === pagamento.idIntermediadorPagamento);

        return intermediador?.bandeiras.find((b) => b.idBandeira === pagamento.idIntermediadorBandeira)?.descricao
            || intermediador?.condicoesComerciais?.find((c) => c.idBandeira === pagamento.idIntermediadorBandeira)?.descricaoBandeira
            || '–';
    }

    public get nomeFormaEnvio(): string {
        return this.dados.nomeFormaEnvio || '';
    }

    // ---- Cliente ----

    public get nomeCliente(): string {
        return this.cliente?.nome || this.venda?.nomePessoaCliente || '';
    }

    public get documentoCliente(): string {
        return this.cliente?.documento || this.venda?.cpfCnpjPessoaCliente || '';
    }

    public get rotuloDocumentoCliente(): string {
        const idTipoPessoa = this.cliente?.idTipoPessoa ?? this.venda?.idTipoPessoaCliente;

        if (idTipoPessoa === TipoPessoaEnum.Fisica) return 'CPF';
        if (idTipoPessoa === TipoPessoaEnum.Juridica) return 'CNPJ';
        return 'Documento';
    }

    /** "Rua, Nº 10, Sala 2. Bairro: Centro" */
    public get enderecoClienteLinha1(): string {
        const c = this.cliente;
        if (!c) return '';

        const rua = [c.logradouro, c.numero ? `Nº ${c.numero}` : null, c.complemento].filter((v) => !!v).join(', ');
        return [rua, c.bairro ? `Bairro: ${c.bairro}` : null].filter((v) => !!v).join('. ');
    }

    /** "39404-138 - Montes Claros, MG" (CEP no mesmo formato do emitente). */
    public get enderecoClienteLinha2(): string {
        const c = this.cliente;
        if (!c) return '';

        return [this.formatarCep(c.cep), [c.municipio, c.uf].filter((v) => !!v).join(', ')].filter((v) => !!v).join(' - ');
    }

    /** O endereço do consumidor do recibo, em linha corrida. */
    public get enderecoConsumidor(): string {
        const c = this.cliente;
        if (!c) return '';

        return [[c.logradouro, c.numero].filter((v) => !!v).join(', '), c.complemento, c.bairro, c.municipio, c.uf, this.formatarCep(c.cep)]
            .filter((v) => !!v)
            .join(' - ');
    }

    private formatarCep(cep: string | null): string {
        const digitos = (cep || '').replace(/\D/g, '');
        return digitos.length === 8 ? `${digitos.substring(0, 5)}-${digitos.substring(5)}` : cep || '';
    }

    // ---- Impressão ----

    /**
     * Abre a janela de impressão do navegador com a folha, onde o usuário imprime ou salva em PDF. Devolve
     * `false` sem fazer nada quando o navegador bloqueia a janela; `aoAbrir` roda só com a janela aberta (a
     * prévia do sistema grava a ocorrência ali). Resolve depois do `print()`.
     */
    public imprimir(aoAbrir?: () => void): Promise<boolean> {
        const papel = this.elementRef.nativeElement.querySelector('.vfl-papel');
        if (!papel) return Promise.resolve(false);

        const janela = window.open('', '', 'height=600,width=800');
        if (!janela) return Promise.resolve(false);

        aoAbrir?.();

        // A janela nova não herda nada: copia as folhas de estilo da página (as do componente vêm junto, presas
        // aos atributos do encapsulamento). Folha de outra origem lança ao ser lida.
        const estilos = Array.from(document.styleSheets)
            .map((folha) => {
                try {
                    return Array.from(folha.cssRules).map((regra) => regra.cssText).join('');
                } catch {
                    return '';
                }
            })
            .join('');

        janela.document.write(`
            <html>
                <head>
                    <title>${this.tituloDocumento} Nº ${this.numeroDocumento}</title>
                    <base href="/">
                    <style>
                        ${estilos}

                        @page {
                            size: A4 portrait;
                            margin: 12mm 10mm 16mm 10mm;
                            @bottom-right {
                                content: "Página " counter(page) " de " counter(pages);
                                font-family: Arial, Helvetica, sans-serif;
                                font-size: 9px;
                            }
                        }

                        /* A tela é uma viewport travada com rolagem própria; o papel é o oposto. */
                        html, body {
                            height: auto !important;
                            min-height: 0 !important;
                            overflow: visible !important;
                            margin: 0;
                            color: #000;
                            background: #fff;
                        }

                        .no-print { display: none !important; }

                        /* A tabela externa só existe para repetir o cabeçalho: a linha única dela precisa quebrar
                           entre páginas. */
                        .vfl-folha > thead > tr,
                        .vfl-folha > tbody > tr {
                            break-inside: auto !important;
                            page-break-inside: auto !important;
                        }
                    </style>
                </head>
                <body>${papel.innerHTML}</body>
            </html>
        `);

        janela.document.close();
        janela.focus();

        // Só imprime com a janela carregada (estilos, logo e imagens de capa), com teto de 3 s para imagem que não
        // responde. Chamado antes disso, o `print()` do Chrome não segura a execução e a janela fechava na hora; por
        // isso ela também só fecha depois da impressão (`afterprint`).
        const carregada = new Promise<void>((resolve) => {
            if (janela.document.readyState === 'complete') {
                resolve();
                return;
            }
            janela.addEventListener('load', () => resolve(), { once: true });
        });
        const teto = new Promise<void>((resolve) => setTimeout(resolve, 3000));

        return Promise.race([carregada, teto]).then(() => {
            janela.onafterprint = () => janela.close();
            janela.print();
            return true;
        });
    }
}
